// disable the console.* ban for this file because this is a very valid place
// to use console methods
/* eslint-disable ban/ban */
import { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/nextjs';
import { ScopeContext } from '@sentry/types';
import { GraphQLFormattedError } from 'graphql';
import { includes, uniq } from 'lodash';
import { PostHog } from 'posthog-js';

import {
  DebugLevel,
  debugLevelVar,
} from '@/contexts/debugContext/debug.context';
import {
  getGraphQLErrorOperationName,
  getLuminaryErrorCode,
  isGraphQLAuthError,
} from '@/graphql/errors';
import { TenantProductTier, TenantUsageBucket, UserKind } from '@/types/schema';
import { getTenant } from '@/utils/tenantUtils';

import {
  getAppVersion,
  getEnvironment,
  isDevelopmentMode,
} from './environmentUtils';

interface IdentifyUserWithSessionParams {
  email: string;
  firstName: string;
  lastName: string;
  userId: string;
  tenantUsageBucket: TenantUsageBucket;
  tier: TenantProductTier;
  userKind: string;
}

export function identifyUserWithSession(
  posthogClient: PostHog,
  params: IdentifyUserWithSessionParams
) {
  const {
    email,
    firstName,
    lastName,
    userId,
    tenantUsageBucket,
    tier,
    userKind,
  } = params;
  const tenantSubdomain = getTenant();
  Sentry.setUser({
    email,
    id: userId,
  });

  posthogClient.identify(userId, {
    email,
    tenant: tenantSubdomain,
    version: getAppVersion(),
    userKind,
  });

  // don't set the first name and last name for Luminary internal users OR for
  // collaborators
  if (userKind === UserKind.Employee) {
    posthogClient.setPersonProperties(
      // the first argument is the properties to set
      // which are merged with the existing properties. use this instead of the "set once"
      // method for two reasons:
      // 1. we want to update the name of it changes (unlikely)
      // 2. we want to set the name in posthog even if we have seen the user before, because
      // it's possible that we saw the user before we started storing the name, so we want
      // to make sure we do set it.
      {
        firstName,
        lastName,
      }
    );
  }

  if (!includes(email, '@withluminary.com')) {
    posthogClient.group('company', tenantSubdomain, {
      name: tenantSubdomain,
      usageBucket: tenantUsageBucket,
      tier,
    });
  }

  Sentry.setTag(
    'tenant_category',
    isSyntheticTraffic(email) ? 'test' : undefined
  );
}

/**
 * @description Error logger for GraphQL network errors
 */
function logGraphQLNetworkError({
  description,
  error,
  tags,
  fingerprint,
}: {
  description: string;
  error?: Error | null;
  tags: ScopeContext['tags'];
  fingerprint?: ScopeContext['fingerprint'];
}) {
  const augmentedTags = withTenantCategoryTag(tags);
  const { query, ...restTags } = augmentedTags || {};
  const possibleFingerprint = (() => {
    if (fingerprint) {
      return { fingerprint };
    }

    return {};
  })();

  const prettyErrorName = `GraphQLError ${description} ${String(
    tags.operationName
  )}`;
  const captureContext = {
    level: 'error' as const,
    ...possibleFingerprint,
    tags: {
      description,
      ...restTags,
    },
    contexts: {
      GraphQL: {
        description,
        message: error?.message,
        fingerprint,
        ...tags,
      },
    },
  };

  console.error(error, {
    errorName: prettyErrorName,
    ...captureContext,
  });

  Sentry.captureMessage(prettyErrorName, captureContext);
}

function logError({
  description,
  error,
  tags,
  fingerprint,
}: {
  description: string;
  error?: Error | null;
  tags?: ScopeContext['tags'];
  fingerprint?: ScopeContext['fingerprint'];
}) {
  if (tags?.operationName) {
    // this is a GraphQL network error
    logGraphQLNetworkError({
      description,
      error,
      tags: withTenantCategoryTag(tags) as ScopeContext['tags'],
      fingerprint,
    });
  } else {
    const finalError = error || new Error(description);
    const captureContext = {
      fingerprint,
      tags: {
        description,
        ...withTenantCategoryTag(tags),
      },
    };

    console.error(finalError, captureContext);
    Sentry.captureException(finalError, captureContext);
  }
}

const UNKNOWN_ERROR_CODE = 'UNKNOWN_CODE';

interface GraphQLErrorSummary {
  code: string;
  /** Response path with array indices replaced by `$index`. */
  path: string;
  message: string;
}

/**
 * @description Reduces a GraphQL error to the parts that are stable across
 * occurrences, so they are safe to group on. The message is included for
 * context only; it can contain record-specific text.
 */
export function summarizeGraphQLError(
  err: GraphQLFormattedError
): GraphQLErrorSummary {
  const path = (err.path ?? [])
    .map((v: string | number) => (typeof v === 'number' ? '$index' : v))
    .join(' > ');

  return {
    code: getLuminaryErrorCode(err)?.display ?? UNKNOWN_ERROR_CODE,
    path,
    message: err.message,
  };
}

/**
 * @description Builds the Sentry fingerprint and title for a failed GraphQL
 * operation. All errors from one response share one event, so a request that
 * fails at several fields does not produce several issues.
 *
 * The fingerprint uses the operation name, the backend error codes, and the
 * response paths. It does not use the call-site description or the error
 * message, because the description is not part of the failure and the message
 * is the same generic text for every 5xx.
 */
export function getGraphQLErrorGrouping({
  errors,
  description,
  taggedOperationName,
}: {
  errors: readonly GraphQLFormattedError[];
  description: string;
  /** Operation name the caller already knows, e.g. from an Apollo link. */
  taggedOperationName?: string;
}): {
  fingerprint: string[];
  title: string;
  operationName: string;
  summaries: GraphQLErrorSummary[];
} {
  const summaries = errors.map(summarizeGraphQLError);

  // Every error in one response comes from the same operation, so the first
  // stamped name is the operation name. Errors that did not pass through
  // `annotateGraphQLErrorsLink` (subscription errors, hand-built ApolloErrors)
  // carry no stamp, so use the caller's tag next. Fall back to the description
  // last so that unrelated call sites still stay apart.
  const operationName =
    errors.map(getGraphQLErrorOperationName).find(Boolean) ??
    taggedOperationName ??
    `unknown-operation:${description}`;

  const codes = uniq(summaries.map((s) => s.code)).sort();
  const paths = uniq(summaries.map((s) => s.path)).sort();

  return {
    fingerprint: ['graphql', operationName, codes.join(','), paths.join(',')],
    title: `GraphQLError ${codes.join(', ')} in ${operationName}`,
    operationName,
    summaries,
  };
}

/**
 * @description Error logger for GraphQL errors returned from the backend.
 * Sends one Sentry event for the whole failed operation.
 */
function logGraphQLErrors({
  error,
  description,
  tags,
}: {
  error: ApolloError;
  description: string;
  tags?: ScopeContext['tags'];
}) {
  // Match the network link's auth-error suppression, but keep actionable
  // errors when the same response also contains an auth error.
  const errors = error.graphQLErrors.filter(
    (graphQLError) => !isGraphQLAuthError(graphQLError)
  );
  // Network-only errors belong to `reportNetworkErrorsLink`. Responses with
  // only expected auth errors do not need an app-error report either.
  if (errors.length === 0) return;

  const augmentedTags = withTenantCategoryTag(tags);
  // Tags are Sentry primitives; only a string is a usable operation name.
  const taggedOperationName =
    typeof augmentedTags.operationName === 'string'
      ? augmentedTags.operationName
      : undefined;
  const { fingerprint, title, operationName, summaries } =
    getGraphQLErrorGrouping({ errors, description, taggedOperationName });

  Sentry.withScope((scope) => {
    const errorTags: ScopeContext['tags'] = {
      errorType: 'GraphQLError',
      description,
      operationName,
      luminaryCode: uniq(summaries.map((s) => s.code)).join(','),
      ...augmentedTags,
    };

    scope.setTags({ ...errorTags });

    uniq(summaries.map((s) => s.path))
      .filter(Boolean)
      .forEach((path) => {
        scope.addBreadcrumb({
          category: 'execution-path',
          message: path,
          level: 'debug',
        });
      });

    // Sentry flattens nested objects past a shallow depth and shows
    // "[Object]", so store each error as one line of text.
    const errorLines = Object.fromEntries(
      summaries.map((s, i) => [
        `error_${i}`,
        `${s.code} at ${s.path || '<operation>'}: ${s.message}`,
      ])
    );

    const captureContext = {
      level: 'error' as const,
      fingerprint,
      contexts: {
        GraphQL: {
          description,
          operationName,
          errorCount: summaries.length,
          ...errorLines,
        },
      },
    };

    console.error(error, {
      ...captureContext,
      errorName: title,
      tags: errorTags,
    });

    Sentry.captureMessage(title, captureContext);
  });
}

/**
 *
 * @param description A human-friendly description of the context of the issue
 * @param error The underlying associated error. If no error is generated, you can create your own. This is useful because it make sure there's always an associated stacktrace.
 * @param context Any additional parameters that would be useful for debugging.
 */
export function error(
  description: string,
  error?: Error | null,
  tags?: ScopeContext['tags'],
  fingerprint?: ScopeContext['fingerprint']
) {
  const augmentedTags = withTenantCategoryTag(tags);
  if (error instanceof ApolloError) {
    logGraphQLErrors({
      error,
      description,
      tags: augmentedTags,
    });
  } else {
    logError({
      description,
      error: error as Error | null,
      tags: augmentedTags,
      fingerprint,
    });
  }
}

export function trace(message: unknown, data?: object) {
  if (isDevelopmentMode()) {
    console.trace(message, data);
  }
}

export function debug(message: unknown, data?: object) {
  if (isDevelopmentMode() || debugLevelVar() === DebugLevel.VERBOSE) {
    console.debug(message, data);
  }
}

export function info(message: unknown, data?: object) {
  console.info(message, data);

  Sentry.addBreadcrumb({
    level: 'info',
    message: message as string,
    data,
  });
}

export function warn(message?: unknown, data?: object) {
  console.warn(message, data);
  Sentry.addBreadcrumb({
    level: 'warning',
    message: message as string,
    data,
  });
}

// Returns true if the current tenant appears to be a Playwright/integration test tenant
export function isTestTenant() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const tenantSubdomain = getTenant();
    return tenantSubdomain.startsWith('test-');
  } catch {
    return false;
  }
}

function isSyntheticTraffic(email?: string) {
  if (isTestTenant()) {
    return true;
  }

  const emailDomain = email?.split('@').at(-1)?.toLowerCase();
  return (
    emailDomain === 'mailosaur.net' || emailDomain?.endsWith('.mailosaur.net')
  );
}

/**
 * Merge the provided tags with the tenant_category tag when applicable.
 * Ensures we consistently tag events originating from integration-test tenants so
 * that they can be filtered out easily in Sentry.
 */
function withTenantCategoryTag(
  tags: ScopeContext['tags'] | undefined
): ScopeContext['tags'] {
  // Ensure we always return an object so callers can safely spread the result
  const baseTags: ScopeContext['tags'] = tags ?? {};

  if (!isSyntheticTraffic()) {
    return baseTags;
  }

  return {
    ...baseTags,
    tenant_category: 'test',
  };
}

export const diagnostics = {
  error,
  warn,
  debug,
  trace,
  info,
  isDevelopmentMode,
  identifyUserWithSession,
  getEnvironment,
  getAppVersion,
};
